import React, { useMemo } from "react"
import { useDocumentTitle } from "@/hooks/useDocumentTitle"
import { NavBar } from "@/components/navbar"
import BusinessNotFound from "@/components/business/details/BusinessNotFound"
import BusinessContent from "@/components/business/details/BusinessContent"
import useBusinessDetailsState from "@/hooks/useBusinessDetailsState"
import { useNavigate } from "react-router-dom"
import { LoadingSpinner } from "@/components/auth/components/LoadingSpinner"
import { useToast } from "@/hooks/use-toast"

interface BusinessDashboardProps {
  pageTitle?: string
}

const BusinessDashboard: React.FC<BusinessDashboardProps> = ({
  pageTitle = "Business Dashboard",
}) => {
  const navigate = useNavigate()
  const { toast } = useToast()

  const {
    business,
    portfolioTitle,
    timeFrame,
    setTimeFrame,
    searchTerm,
    setSearchTerm,
    leads,
    metricsRefreshKey,
    handleStatusChange,
    handleOutcomeChange,
    handleValueChange,
    handleNameChange,
    handleEditCampaign,
    handleEditGoPlan,
    handleEditGoCampaign,
    handleFilter,
    handleSearch,
    businessLoading,
    hasAccess,
  } = useBusinessDetailsState()
  
  // Calculate the title based on business data
  const documentTitle = useMemo(() => {
    if (!business) {
      return "Business Dashboard - Gozango App";
    }
    const businessDisplayName = business.title || business.name || pageTitle;
    return `${businessDisplayName} - Gozango App`;
  }, [business, pageTitle]);
  
  // Set the document title
  useDocumentTitle(documentTitle);

  // Redirect to home page if user doesn't have access to this business
  // useEffect(() => {
  //   if (hasAccess === false) {
  //     toast({
  //       title: "Access Denied",
  //       description: "You don't have access to this business.",
  //       variant: "destructive",
  //     })
  //     navigate("/", { replace: true })
  //   }
  // }, [hasAccess, navigate, toast])

  // Show loading spinner while checking access or loading business data
  if (businessLoading || hasAccess === null) {
    return <LoadingSpinner />
  }

  if (!business || hasAccess === false) {
    return <BusinessNotFound />
  }

  // Get the actual business name to display in the navbar
  const businessDisplayName = business?.title || business?.name || pageTitle

  // Create a consistent navigation handler for business settings
  const handleNavigateToSettings = () => {
    console.log("Navigate to settings clicked in BusinessDashboard")
    if (business && business.id) {
      // Use navigate directly instead of window.location
      console.log(
        `REDIRECTING: Navigating to /business/${business.id}/settings using React Router`
      )
      navigate(`/business/${business.id}/settings`, { replace: true })
    }
  }

  return (
    <div className="min-h-screen bg-brand-light flex flex-col">
      <NavBar
        businessName={businessDisplayName}
        timeFrame={timeFrame}
        onTimeFrameChange={setTimeFrame}
        handleEditCampaign={handleEditCampaign}
        handleEditGoPlan={handleEditGoPlan}
        handleEditGoCampaign={handleEditGoCampaign}
        handleNavigateToSettings={handleNavigateToSettings}
      />

      <BusinessContent
        businessId={business.id}
        leads={leads}
        timeFrame={timeFrame}
        onTimeFrameChange={setTimeFrame}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        onStatusChange={handleStatusChange}
        onOutcomeChange={handleOutcomeChange}
        onValueChange={handleValueChange}
        onNameChange={handleNameChange}
        metricsRefreshKey={metricsRefreshKey}
        onEditCampaign={handleEditCampaign}
      />
    </div>
  )
}

export default BusinessDashboard
